import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const AdminKpikriTab = ({ user }) => {
  const { tokenManager } = useAuth();
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKPIs();
  }, [user]);

  const fetchKPIs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/kpis/user/${user?._id || user?.id}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setKpis(data.kpis || []);
      } else {
        // If no KPIs endpoint exists, show placeholder
        setKpis([]);
      }
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      setKpis([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {user?.firstName} {user?.lastName}'s KPI/KRA
      </Typography>
      
      {kpis.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No KPI/KRA data available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                KPI and KRA information will be displayed here when available
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Performance Metrics
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric</TableCell>
                        <TableCell>Target</TableCell>
                        <TableCell>Current</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Progress</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kpis.map((kpi, index) => (
                        <TableRow key={index}>
                          <TableCell>{kpi.name}</TableCell>
                          <TableCell>{kpi.target}</TableCell>
                          <TableCell>{kpi.current}</TableCell>
                          <TableCell>
                            <Chip 
                              label={kpi.status || 'In Progress'} 
                              color={kpi.status === 'Achieved' ? 'success' : 'primary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TrendingUpIcon fontSize="small" />
                              {kpi.progress || '0'}%
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AdminKpikriTab;
